import { SELECT_COUNTRY_CODES } from "../assets/const/countries.const";

/**
 * Formats a phone number string into XXX-XXX-XXXX format for US phone numbers.
 * Used on Broadband Signup page and Wireless Signup page.
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number in XXX-XXX-XXXX format.
 */
export const formatUSPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, "");

  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  } else {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6, 10)}`;
  } 
};

export const formatNonUSPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, "");

  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
      3,
      7
    )}-${phoneNumber.slice(7, 11)}`;
  }
};

/**
 * Formats a phone number string into 1 xxxxxxxxxx format.
 * Used on selfcare MyInfo page updating Customer call.
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number in 1 xxxxxxxxxx format.
 */
export const formatPhoneNumberForApi = (
  phoneNumber: string,
  countryCode?: string
): string => {
  // Get the code portion from the human friendly country code string
  const code = countryCode ? countryCode.split('(+')[1]?.split(')')[0] : SELECT_COUNTRY_CODES.default.code;
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  // Ensure the number has 10 (US) or 11 (non US) digits
  if (digitsOnly.length !== 10 && digitsOnly.length !== 11) {
    throw new Error(`The phone number format looks a bit odd. Must be in ${code === '1' ? 'xxx-xxx-xxxx' : 'xxx-xxxx-xxxx'} format. Please try again.`);
  }
  // Return formatted phone number with country code
  return `${code} ${digitsOnly}`;
};

/**
 * Formats a phone number string into 1 xxx-xxx-xxxx or (xx) xxx-xxxx-xxxx format.
 * Used on AuthFields PhoneNumberField for formatting saved phone number from API.
 * @param {string} phoneNumber - The phone number with country code to format.
 * @returns {string} The formatted phone number in 1 xxx-xxx-xxxx or (xx) xxx-xxxx-xxxx format.
 */
export const formatPhoneNumberFromApi = (phoneNumber: string) => {
    if (phoneNumber.split(" ").length > 1) {
      const isUSPhoneNumber = phoneNumber.split(" ")[0] === SELECT_COUNTRY_CODES.default.code.toString();
      return isUSPhoneNumber ? formatUSPhoneNumber(phoneNumber.split(" ")[1])
          : formatNonUSPhoneNumber(phoneNumber.split(" ")[1])  
    }
    // if formatting fails return unmodified phone number
    return phoneNumber;
  }

/**
 * Formats a phone number's country code string into <NAME> (+<CODE>) format.
 * Used on selfcare Checkout page updating Customer call.
 * @param {object} code - The phone number to format.
 * @returns {string} The formatted country code in <NAME> (+<CODE>) format.
 */
type CountryCode = {
  name: string;
  code: number;
};

/**
 * Formats a phone number string into 'COUNTRY_CODE_NAME (+code)' format.
 * Used on selfcare MyInfo page.
 * @param {CountryCode} codeObj - Object with name and code values, to format.
 * @returns {string} The formatted country code in 'COUNTRY_CODE_NAME (+code)' format.
 */
export const formatCountryCode = (codeObj: CountryCode): string => {
  const { name, code } = codeObj;
  return `${name} (+${code})`;
};

/**
 * Formats a phone number string into (X) XXX-XXX-XXXX format.
 * Used on selfcare MyInfo page.
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number in (X) XXX-XXX-XXXX format.
 */
export const formatPhoneNumberWithCountryCode = (phoneNumber: string) => {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Check if the number has 11 digits (including country code)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 1)}) ${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7,)}`;
  }

  // If it's a 10-digit number (without country code)
  if (cleaned.length === 13) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 5)}-${cleaned.slice(5, 9)}-${cleaned.slice(9,)}`;
  }

  // If the number doesn't match expected formats, return the original
  return phoneNumber;
};

/**
 * Formats a phone number string into XXX-XXX-XXXX format.
 * Used on Eternet Fiber Signup page.
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number in XXX-XXX-XXXX format.
 */
export const formatPhoneNumberForEternetFiber = (event: any) => {
  var phoneValue = event.replace(/[^\d]*/g, "");
  // remove non-digits
  phoneValue = phoneValue.replace(
    /(\d{1,3})(\d{1,3})?(\d{1,4})?.*/g,
    "$1 $2 $3"
  );
  phoneValue = phoneValue.trim();
  phoneValue = phoneValue.replace(/\s+/g, "-");
  return phoneValue;
};
